import { useState, useEffect, useRef } from 'react'
import { estimatesService, type FieldNote } from '@/services/estimates-service'
import { printProjectNotes } from '@/lib/print-notes'

interface FieldNotesPanelProps {
  estimateId: string
  open: boolean
  onClose: () => void
  projectName?: string
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function FieldNotesPanel({ estimateId, open, onClose, projectName }: FieldNotesPanelProps) {
  const [notes, setNotes] = useState<FieldNote[]>([])
  const [loading, setLoading] = useState(true)
  const [newNote, setNewNote] = useState('')
  const [posting, setPosting] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        const data = await estimatesService.getFieldNotes(estimateId)
        if (!cancelled) setNotes(data)
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [estimateId, open])

  // Focus textarea when drawer opens
  useEffect(() => {
    if (open && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 300)
    }
  }, [open])

  async function handleAdd() {
    const text = newNote.trim()
    if (!text || posting) return
    setPosting(true)
    try {
      const note = await estimatesService.addFieldNote(estimateId, text)
      setNotes(prev => [note, ...prev])
      setNewNote('')
      // Scroll to top to show new note
      if (listRef.current) listRef.current.scrollTop = 0
    } catch {
      // silent
    } finally {
      setPosting(false)
    }
  }

  async function handleDelete(noteId: string) {
    setDeletingId(noteId)
    try {
      await estimatesService.deleteFieldNote(estimateId, noteId)
      setNotes(prev => prev.filter(n => n.id !== noteId))
    } catch {
      // silent
    } finally {
      setDeletingId(null)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAdd()
    }
  }

  // Text-only notes (exclude photo notes from drawer)
  const textNotes = notes.filter(n => !n.photoAssetId)

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fn-drawer-backdrop ${open ? 'fn-drawer-backdrop-open' : ''}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`fn-drawer ${open ? 'fn-drawer-open' : ''}`}>
        {/* Header */}
        <div className="fn-drawer-header">
          <div className="fn-drawer-title">
            <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" width="20" height="20">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" strokeLinecap="round" strokeLinejoin="round" />
              <rect x="9" y="3" width="6" height="4" rx="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Project Notes</span>
          </div>
          <div className="fn-drawer-header-actions">
            <button
              className="fn-print-btn"
              onClick={() => printProjectNotes({ projectName, notes })}
              disabled={loading}
              title="Print these notes (internal — not for the customer)"
            >
              <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path d="M6 9V3h12v6" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="6" y="13" width="12" height="8" rx="1" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M6 17H4a2 2 0 01-2-2v-2a2 2 0 012-2h16a2 2 0 012 2v2a2 2 0 01-2 2h-2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Print</span>
            </button>
            <button className="fn-drawer-close" onClick={onClose}>
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="20" height="20">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Internal-only reassurance banner */}
        <div className="fn-internal-banner">
          <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <rect x="4" y="10" width="16" height="10" rx="2" />
            <path d="M8 10V7a4 4 0 018 0v3" strokeLinecap="round" />
          </svg>
          <div><strong>Internal notes — staff only.</strong> These are for your team. They're never shown to the customer or on the proposal.</div>
        </div>

        {/* Input area */}
        <div className="fn-drawer-input">
          <textarea
            ref={textareaRef}
            className="fn-input"
            placeholder="Add a note..."
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
            disabled={posting}
          />
          <button
            className="fn-add-btn"
            onClick={handleAdd}
            disabled={!newNote.trim() || posting}
          >
            {posting ? 'Adding...' : 'Add Note'}
          </button>
        </div>

        {/* Notes list */}
        <div className="fn-drawer-list" ref={listRef}>
          {loading ? (
            <div className="fn-loading">Loading notes…</div>
          ) : textNotes.length === 0 ? (
            <div className="fn-empty">
              <p>No project notes yet</p>
              <span>Add a note above or capture notes from the mobile app</span>
            </div>
          ) : (
            <div className="fn-section">
              <div className="fn-notes-list">
                {textNotes.map(note => (
                  <div key={note.id} className={`fn-note-card${note.inherited ? ' fn-note-card-inherited' : ''}`}>
                    <div className="fn-note-header">
                      <span className="fn-note-author">{note.authorName}</span>
                      <span className="fn-note-date">{formatDate(note.createdAt)}</span>
                    </div>
                    {/* Carried over from the bid being revised. It lives on that
                        estimate, so it is shown here but not editable — the
                        delete button would only 404. */}
                    {note.inherited && (
                      <span className="fn-note-inherited" title="Written on the original bid this revision came from">
                        From the original bid
                      </span>
                    )}
                    <p className="fn-note-text">{note.text}</p>
                    {!note.inherited && (
                      <button
                        className="fn-note-delete"
                        onClick={() => handleDelete(note.id)}
                        disabled={deletingId === note.id}
                        title="Delete note"
                      >
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
